import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../api";

function StatusBadge({ status }) {
  const cls =
    status === "success" ? "badge-green" :
    status === "running" ? "badge-blue" :
    status === "error" ? "badge-gray" : "badge-yellow";
  return <span className={"badge " + cls} data-testid="sync-status-badge">{status}</span>;
}

export default function Products() {
  const [sync, setSync] = useState(null);
  const [connection, setConnection] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const [tab, setTab] = useState("products");
  const [pSearch, setPSearch] = useState("");
  const [cSearch, setCSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);

  const loadStatus = async () => {
    try {
      const res = await api.get("/sync/status");
      setSync(res.data);
    } catch (_) {}
  };

  const loadConnection = async () => {
    try {
      const res = await api.get("/shopify/connection");
      setConnection(res.data);
    } catch (_) {}
  };

  const loadProducts = async () => {
    const res = await api.get(`/products?search=${encodeURIComponent(pSearch)}`);
    setProducts(res.data);
  };

  const loadCollections = async () => {
    const res = await api.get(`/collections?search=${encodeURIComponent(cSearch)}`);
    setCollections(res.data);
  };

  useEffect(() => {
    loadStatus();
    loadConnection();
    loadProducts();
    loadCollections();
  }, []);

  const runSync = async () => {
    setSyncing(true);
    setError("");
    try {
      const res = await api.post("/sync/shopify");
      setSync(res.data);
      await Promise.all([loadProducts(), loadCollections()]);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const connectShopify = () => {
    window.location.assign(`${api.defaults.baseURL}/shopify/auth`);
  };

  return (
    <div data-testid="products-page">
      <header className="page-head">
        <h1>Shopify Catalog</h1>
        <button
          className="btn btn-primary"
          data-testid="sync-shopify-btn"
          onClick={runSync}
          disabled={syncing}
        >
          {syncing ? "Syncing…" : "Sync Shopify products"}
        </button>
      </header>

      {error && <div className="alert" data-testid="sync-error">{error}</div>}

      <div className="card" data-testid="shopify-connection" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
          <div>
            <div className="cell-strong">Shopify</div>
            <div>
              {connection?.connected ? "Connected" : "Not connected"}
              {connection?.connected && connection.shopDomain ? ` — ${connection.shopDomain}` : ""}
            </div>
            {connection?.connected && (
              <div className="muted" style={{ marginTop: 4 }}>
                Granted scopes: {(connection.grantedScopes || []).join(", ") || "None"}
              </div>
            )}
          </div>
          {connection && (!connection.connected || (connection.missingScopes || []).length > 0) && (
            <button className="btn btn-primary" onClick={connectShopify} data-testid="connect-shopify-btn">
              {connection.connected ? "Reconnect Shopify" : "Connect Shopify"}
            </button>
          )}
        </div>
      </div>

      <div className="metric-grid">
        <div className="metric-card" data-testid="sync-state-card">
          <div className="metric-label">Sync Status</div>
          <div className="metric-value">{sync ? <StatusBadge status={sync.status} /> : "…"}</div>
        </div>
        <div className="metric-card" data-testid="sync-last">
          <div className="metric-label">Last Sync</div>
          <div className="metric-value" style={{ fontSize: 16 }}>
            {sync && sync.lastSyncAt ? new Date(sync.lastSyncAt).toLocaleString() : "—"}
          </div>
        </div>
        <div className="metric-card" data-testid="sync-product-count">
          <div className="metric-label">Products</div>
          <div className="metric-value">{sync ? sync.productCount ?? 0 : "…"}</div>
        </div>
        <div className="metric-card" data-testid="sync-collection-count">
          <div className="metric-label">Collections</div>
          <div className="metric-value">{sync ? sync.collectionCount ?? 0 : "…"}</div>
        </div>
      </div>
      {sync && sync.error && (
        <div className="alert" data-testid="sync-error-detail" style={{ marginTop: 12 }}>
          {sync.error}
        </div>
      )}

      <div className="tabs" style={{ marginTop: 24, display: "flex", gap: 8 }}>
        <button
          className={"btn " + (tab === "products" ? "btn-primary" : "btn-ghost")}
          data-testid="tab-products"
          onClick={() => setTab("products")}
        >
          Products
        </button>
        <button
          className={"btn " + (tab === "collections" ? "btn-primary" : "btn-ghost")}
          data-testid="tab-collections"
          onClick={() => setTab("collections")}
        >
          Collections
        </button>
      </div>

      {tab === "products" ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              data-testid="product-search"
              placeholder="Search products by title, handle or ID"
              value={pSearch}
              onChange={(e) => setPSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadProducts()}
              style={{ flex: 1 }}
            />
            <button className="btn btn-ghost" data-testid="product-search-btn" onClick={loadProducts}>
              Search
            </button>
          </div>
          {products.length === 0 ? (
            <div className="empty" data-testid="products-empty">
              No products. Click “Sync Shopify products”.
            </div>
          ) : (
            <div className="table-wrap">
              <table data-testid="products-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Legacy ID</th>
                    <th>Status</th>
                    <th>Category</th>
                    <th>Collections</th>
                    <th>Ships</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} data-testid={`product-row-${p.legacyId || p.id}`}>
                      <td className="cell-strong">{p.title}</td>
                      <td><code>{p.legacyId || "—"}</code></td>
                      <td>{p.status}</td>
                      <td>{p.taxonomyCategoryName || "—"}</td>
                      <td>{(p.collectionIds || []).length}</td>
                      <td>{p.requiresShipping ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              data-testid="collection-search"
              placeholder="Search collections by title or handle"
              value={cSearch}
              onChange={(e) => setCSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadCollections()}
              style={{ flex: 1 }}
            />
            <button className="btn btn-ghost" data-testid="collection-search-btn" onClick={loadCollections}>
              Search
            </button>
          </div>
          {collections.length === 0 ? (
            <div className="empty" data-testid="collections-empty">No collections synced yet.</div>
          ) : (
            <div className="table-wrap">
              <table data-testid="collections-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Handle</th>
                    <th>Legacy ID</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map((c) => (
                    <tr key={c.id} data-testid={`collection-row-${c.legacyId || c.id}`}>
                      <td className="cell-strong">{c.title}</td>
                      <td>{c.handle || "—"}</td>
                      <td><code>{c.legacyId || "—"}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
